import { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { ArrowLeft, ArrowRight, Check, Leaf } from "lucide-react";
import { toast } from "sonner";
import { Logo, Wordmark } from "@/components/Logo";
import { Reveal, MaskedLine, EASE } from "@/components/Reveal";
import { fetchQuestions, subscribe } from "@/lib/api";

const MARQUEE_WORDS = [
  "soft light",
  "deep exhale",
  "quiet corners",
  "weighted calm",
  "gentle textures",
  "room to breathe",
  "warm wood",
  "low volume",
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const OptionCard = ({ qid, oid, label, selected, onSelect }) => (
  <button
    type="button"
    data-testid={`option-${qid}-${oid}`}
    onClick={() => onSelect(oid)}
    className={`w-full rounded-2xl border px-6 py-5 text-left text-base transition-colors duration-300 ${
      selected
        ? "bg-sage-700 border-sage-700 text-paper"
        : "bg-white border-sage-200 text-ink hover:border-sage-500"
    }`}
  >
    <span className="flex items-center justify-between gap-4">
      <span>{label}</span>
      <span
        className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border transition-colors duration-300 ${
          selected ? "border-cream bg-cream/20" : "border-sage-200"
        }`}
      >
        {selected && <Check className="h-3 w-3 text-cream" strokeWidth={3} />}
      </span>
    </span>
  </button>
);

const QuestionFlow = ({ email, onBack, onDone }) => {
  const [questions, setQuestions] = useState(null);
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const timer = useRef(null);
  const reduce = useReducedMotion();

  useEffect(() => {
    fetchQuestions()
      .then(setQuestions)
      .catch(() => toast.error("We couldn't load the questions. Please refresh."));
    return () => clearTimeout(timer.current);
  }, []);

  const current = questions && questions[step];
  const total = questions ? questions.length : 0;
  const progress = total ? (step / total) * 100 : 0;

  const select = (oid) => {
    if (!current) return;
    setAnswers((a) => ({ ...a, [current.id]: oid }));
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      if (step < total - 1) setStep((s) => s + 1);
    }, reduce ? 0 : 420);
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      await subscribe(email, answers);
      onDone();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(detail || "Something wobbled. Please try again in a moment.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!questions) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-moss animate-pulse">Taking a slow breath…</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col px-6 pt-24 pb-16">
      <div className="w-full max-w-2xl mx-auto flex-1 flex flex-col justify-center">
        <div data-testid="flow-progress-bar" className="h-1 w-full rounded-full bg-sage-100 overflow-hidden mb-10">
          <motion.div
            className="h-full rounded-full bg-sage-700"
            initial={false}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.7, ease: EASE }}
            style={{ width: `${progress}%` }}
          />
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={current.id}
            initial={reduce ? { opacity: 0 } : { opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={reduce ? { opacity: 0 } : { opacity: 0, y: -16 }}
            transition={{ duration: 0.55, ease: EASE }}
          >
            <p className="text-xs tracking-[0.18em] uppercase text-sage-700 mb-4" data-testid="flow-step-label">
              {step + 1} of {total}
            </p>
            <h1 className="font-serif text-3xl sm:text-4xl leading-tight text-ink mb-3">{current.title}</h1>
            <p className="text-moss mb-10">{current.hint}</p>
            <div className="flex flex-col gap-4">
              {current.options.map((o) => (
                <OptionCard
                  key={o.id}
                  qid={current.id}
                  oid={o.id}
                  label={o.label}
                  selected={answers[current.id] === o.id}
                  onSelect={select}
                />
              ))}
            </div>
          </motion.div>
        </AnimatePresence>

        <div className="flex items-center justify-between mt-12">
          {step > 0 ? (
            <button
              type="button"
              data-testid="flow-back-button"
              onClick={() => setStep((s) => Math.max(0, s - 1))}
              className="flex items-center gap-2 text-moss hover:text-ink transition-colors duration-300"
            >
              <ArrowLeft className="h-4 w-4" /> Back
            </button>
          ) : (
            <button
              type="button"
              data-testid="flow-cancel-button"
              onClick={onBack}
              className="flex items-center gap-2 text-moss hover:text-ink transition-colors duration-300"
            >
              <ArrowLeft className="h-4 w-4" /> Back
            </button>
          )}

          {step < total - 1 ? (
            <button
              type="button"
              data-testid="flow-next-button"
              disabled={!answers[current.id]}
              onClick={() => setStep((s) => s + 1)}
              className={`flex items-center gap-2 rounded-full px-8 py-3.5 text-sm font-semibold transition-colors duration-300 ${
                answers[current.id]
                  ? "bg-sage-700 text-paper hover:bg-sage-900"
                  : "bg-sage-100 text-moss cursor-not-allowed"
              }`}
            >
              Next <ArrowRight className="h-4 w-4" />
            </button>
          ) : (
            <button
              type="button"
              data-testid="flow-submit-button"
              disabled={submitting}
              onClick={submit}
              className="flex items-center gap-2 rounded-full bg-sage-700 px-8 py-3.5 text-sm font-semibold text-paper hover:bg-sage-900 transition-colors duration-300 disabled:opacity-60"
            >
              {submitting ? "Sending gently…" : "Begin the letters"} {!submitting && <ArrowRight className="h-4 w-4" />}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const Marquee = () => (
  <div className="bg-ink text-cream py-6 overflow-hidden" aria-label="Calming words">
    <div className="marquee-track">
      {[0, 1].map((half) => (
        <div key={half} className="flex items-center shrink-0" aria-hidden={half === 1}>
          {MARQUEE_WORDS.map((w) => (
            <span key={`${half}-${w}`} className="flex items-center">
              <span className="font-serif italic text-xl px-8 whitespace-nowrap">{w}</span>
              <span className="h-1.5 w-1.5 rounded-full bg-sage-500" />
            </span>
          ))}
        </div>
      ))}
    </div>
  </div>
);

const HeroImage = () => {
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 700], [0, 70]);

  return (
    <div ref={ref} className="relative">
      <div
        className="absolute -top-10 -right-2 sm:-right-8 h-72 w-72 rounded-full bg-sage-100 blur-3xl"
        aria-hidden="true"
      />
      <motion.div style={reduce ? {} : { y }} className="relative">
        <div className="arch-frame overflow-hidden soft-shadow-lift bg-sage-100">
          <img
            src="https://images.pexels.com/photos/4512766/pexels-photo-4512766.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
            alt="Calla lilies catching soft morning light"
            className="w-full h-[420px] sm:h-[520px] object-cover"
            loading="eager"
          />
        </div>
        <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl px-6 py-4 soft-shadow hidden sm:block">
          <p className="font-serif italic text-ink">light, but soft</p>
        </div>
      </motion.div>
    </div>
  );
};

export default function Home() {
  const [view, setView] = useState("landing");
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const emailRef = useRef(null);

  useEffect(() => {
    // Google tag (gtag.js) — landing page only; token URLs never reach analytics.
    if (window.__gtagLoaded) return;
    window.__gtagLoaded = true;
    const script = document.createElement("script");
    script.async = true;
    script.src = "https://www.googletagmanager.com/gtag/js?id=G-09T84LMDMB";
    document.head.appendChild(script);
    window.dataLayer = window.dataLayer || [];
    window.gtag = function () { window.dataLayer.push(arguments); };
    window.gtag("js", new Date());
    window.gtag("config", "G-09T84LMDMB");
  }, []);

  const beginFlow = () => {
    if (!EMAIL_RE.test(email.trim())) {
      setEmailError("Please enter a valid email address.");
      return;
    }
    setEmailError("");
    setView("questions");
  };

  const focusEmail = () => {
    emailRef.current?.focus();
    emailRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  if (view === "questions") {
    return (
      <QuestionFlow
        email={email.trim()}
        onBack={() => setView("landing")}
        onDone={() => setView("done")}
      />
    );
  }

  if (view === "done") {
    return (
      <div data-testid="confirmation-view" className="min-h-screen flex flex-col items-center justify-center px-6 text-center">
        <motion.div
          initial={{ scale: 0.7, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.9, ease: EASE }}
          className="h-20 w-20 rounded-full bg-sage-100 flex items-center justify-center mb-8"
        >
          <Check className="h-8 w-8 text-sage-700" strokeWidth={2.5} />
        </motion.div>
        <h1 className="font-serif text-4xl sm:text-5xl text-ink mb-5">Almost in.</h1>
        <p className="max-w-md text-moss leading-relaxed mb-2">
          We've sent a soft confirmation email to <span className="text-ink font-medium">{email.trim()}</span>.
          Tap "Yes, add me" inside, and your first letter will arrive on a Monday morning.
        </p>
        <p className="max-w-md text-moss leading-relaxed mb-10">
          Nothing after a few minutes? It can take a moment — or sign up again with the same email.
        </p>
        <a href="/" className="link-quiet text-sm">Back to the quiet home page</a>
      </div>
    );
  }

  return (
    <div>
      {/* Nav */}
      <header className="sticky top-0 z-40 bg-paper/90 backdrop-blur-md border-b border-sage-100">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <a href="/" className="flex items-center gap-3" aria-label="AuDHDSpaces home">
            <Logo size={30} />
            <Wordmark />
          </a>
          <nav className="flex items-center gap-8">
            <a href="#how" className="hidden sm:block text-sm text-moss hover:text-ink transition-colors duration-300">
              How it works
            </a>
            <button
              type="button"
              data-testid="nav-subscribe-button"
              onClick={focusEmail}
              className="rounded-full bg-sage-700 px-6 py-2.5 text-sm font-semibold text-paper hover:bg-sage-900 transition-colors duration-300"
            >
              Subscribe
            </button>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-6 pt-16 sm:pt-24 pb-24">
        <div className="grid lg:grid-cols-[1.05fr_0.95fr] gap-16 lg:gap-20 items-center">
          <div>
            <p className="text-xs tracking-[0.18em] uppercase text-sage-700 mb-6">
              A gentle letter for AuDHD homes
            </p>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-[4.2rem] leading-[1.08] text-ink mb-7">
              <MaskedLine delay={0.1}>Come home</MaskedLine>
              <MaskedLine delay={0.28}>
                to <span className="italic text-sage-700">softer.</span>
              </MaskedLine>
            </h1>
            <p className="max-w-prose text-moss text-lg leading-relaxed mb-10">
              AuDHDSpaces is a calm, low-sensory newsletter about decor. Every other Monday
              morning you get a few small, gentle ideas — chosen for what soothes your
              senses, and quietly steered away from what overwhelms.
            </p>

            <div className="max-w-md">
              <div className="flex flex-col sm:flex-row gap-3">
                <input
                  ref={emailRef}
                  type="email"
                  data-testid="hero-email-input"
                  className="email-input"
                  placeholder="your@email.com"
                  value={email}
                  aria-label="Your email address"
                  onChange={(e) => setEmail(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && beginFlow()}
                />
                <button
                  type="button"
                  data-testid="hero-start-button"
                  onClick={beginFlow}
                  className="shrink-0 rounded-full bg-sage-700 px-8 py-4 text-sm font-semibold text-paper hover:bg-sage-900 transition-colors duration-300"
                >
                  Begin
                </button>
              </div>
              {emailError && (
                <p data-testid="email-error" className="text-sm mt-3 text-[#A86454]">{emailError}</p>
              )}
              <p className="text-xs text-moss mt-4">
                One email every two weeks. Unsubscribe in one click, anytime — no login, no fuss.
              </p>
            </div>
          </div>

          <HeroImage />
        </div>
      </section>

      {/* Marquee */}
      <Marquee />

      {/* How it works */}
      <section id="how" className="max-w-6xl mx-auto px-6 py-24 sm:py-32">
        <Reveal>
          <h2 className="font-serif text-3xl sm:text-4xl text-ink mb-4">How it works</h2>
          <p className="text-moss max-w-prose mb-14">Three unhurried steps. That's the whole thing.</p>
        </Reveal>
        <div className="grid md:grid-cols-3 gap-8">
          {[
            {
              n: "01",
              t: "Share your email",
              d: "Just the one field. No password, no account, nothing to remember.",
            },
            {
              n: "02",
              t: "Answer seven soft questions",
              d: "Tell us what calms you — dim light, plush textures, quiet — and what overwhelms. It takes two unhurried minutes.",
            },
            {
              n: "03",
              t: "Receive the letter",
              d: "Every other Monday morning: a few calming decor tips written for your answers. Short, warm, and done.",
            },
          ].map((s, i) => (
            <Reveal key={s.n} delay={i * 0.12} className="h-full">
              <div className="h-full bg-white rounded-3xl p-10 soft-shadow">
                <p className="font-serif italic text-2xl text-sage-500 mb-6">{s.n}</p>
                <h3 className="font-serif text-2xl text-ink mb-3">{s.t}</h3>
                <p className="text-moss leading-relaxed">{s.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Sample letter */}
      <section className="bg-cream py-24 sm:py-32">
        <div className="max-w-6xl mx-auto px-6">
          <Reveal>
            <p className="text-xs tracking-[0.18em] uppercase text-sage-700 mb-4">From a past letter</p>
            <h2 className="font-serif text-3xl sm:text-4xl text-ink mb-4">A taste of the letters</h2>
            <p className="text-moss max-w-prose mb-14">
              Each letter carries only a few ideas, so nothing piles up. Something like this:
            </p>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: <Leaf className="h-5 w-5 text-sage-700" strokeWidth={1.75} />,
                t: "Lower the light",
                d: "Swap one bright ceiling light for a warm lamp at eye level. Lower light tells your nervous system the day is done.",
              },
              {
                icon: <Leaf className="h-5 w-5 text-sage-700" strokeWidth={1.75} />,
                t: "Leave one surface bare",
                d: "A shelf, a corner of the counter. A resting place for your eyes is not empty — it's intentional.",
              },
              {
                icon: <Leaf className="h-5 w-5 text-sage-700" strokeWidth={1.75} />,
                t: "Keep one heavy thing near",
                d: "A weighted blanket on the sofa gives calm you can lean into, even for ten minutes.",
              },
            ].map((c, i) => (
              <Reveal key={c.t} delay={i * 0.12} className="h-full">
                <div className="h-full bg-paper rounded-3xl p-10 border border-sage-100">
                  <div className="h-10 w-10 rounded-full bg-sage-100 flex items-center justify-center mb-6">
                    {c.icon}
                  </div>
                  <h3 className="font-serif text-xl text-ink mb-3">{c.t}</h3>
                  <p className="text-moss leading-relaxed">{c.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="max-w-3xl mx-auto px-6 py-24 sm:py-32 text-center">
        <Reveal>
          <h2 className="font-serif text-4xl sm:text-5xl text-ink mb-5">Ready for a quieter inbox?</h2>
          <p className="text-moss max-w-prose mx-auto mb-10">
            Two soft minutes now, a gentler home every other Monday after.
          </p>
          <button
            type="button"
            data-testid="cta-begin-button"
            onClick={focusEmail}
            className="rounded-full bg-sage-700 px-10 py-4 text-sm font-semibold text-paper hover:bg-sage-900 transition-colors duration-300"
          >
            Begin with your email
          </button>
        </Reveal>
      </section>

      {/* Footer */}
      <footer className="border-t border-sage-100">
        <div className="max-w-6xl mx-auto px-6 py-12 flex flex-col sm:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <Logo size={26} />
            <span className="text-sm text-moss">A gentle letter for AuDHD homes.</span>
          </div>
          <p className="text-xs text-moss text-center sm:text-right">
            Made quietly. Unsubscribe anytime with one click — every email carries the link.
          </p>
        </div>
        <div className="border-t border-sage-100/60">
          <p data-testid="footer-credit" className="text-xs text-moss text-center py-5">
            Developed by{" "}
            <a
              href="https://cusidhe.com"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-credit-link"
              className="link-quiet"
            >
              Cu Sidhe
            </a>{" "}
            llc 2026
          </p>
        </div>
      </footer>
    </div>
  );
}
